import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import { FaUsers, FaFlagCheckered, FaPercentage, FaExclamationTriangle, FaArrowUp } from 'react-icons/fa';
import TableMetric from '../../../containers/Reports/metric';
import ConversionFunnel from '../../Charts/Funnel/conversions';
import ThirdFloorDataGrid from '../../Grids';
import { getDateRange } from '../applyFilters';


function toDate(value) {
    if (!value) return null;

    const dt = new Date(value?.match?.('T') ? value : `${value}T12:00:00`);
    return isNaN(dt) ? null : dt;
}

function normalizeDate(value, endOfDay = false) {
    if (!value) return null;

    if (value instanceof Date) {
        const d = new Date(value);
        if (isNaN(d)) return null;

        if (endOfDay) {
            d.setHours(23, 59, 59, 999);
        } else {
            d.setHours(0, 0, 0, 0);
        }

        return d;
    }

    if (typeof value === 'string') {
        const hasTime = value.includes('T');
        const d = new Date(
            hasTime
                ? value
                : `${value}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}`
        );

        return isNaN(d) ? null : d;
    }

    return null;
}

function isWithinRange(date, startDate, endDate) {
    const target = date instanceof Date ? date : normalizeDate(date);
    const start = normalizeDate(startDate, false);
    const end = normalizeDate(endDate, true);

    if (!target || !start || !end) return false;

    return target >= start && target <= end;
}

function formatPercent(num, den) {
    if (!den) return '0%';
    return `${Math.round((num / den) * 100)}%`;
}

function useWhyDidYouUpdateEffect(name, deps) {
    const prevDepsRef = useRef();

    useEffect(() => {
        if (!prevDepsRef.current) {
            console.log(`${name}: first run`);
            prevDepsRef.current = deps;
            return;
        }

        deps.forEach((dep, index) => {
            const prevDep = prevDepsRef.current[index];

            if (!Object.is(dep, prevDep)) {
                console.log(`${name}: dependency changed`, {
                    index,
                    previous: prevDep,
                    current: dep,
                });
            }
        });

        prevDepsRef.current = deps;
    });
}

export default function CohortConversion({ data = [], filters = {}, reportConfig = {} }) {
    const [ chartData, setChartData ] = useState( [] );
    const [ funnelOrder, setFunnelOrder ] = useState( [] );
    const [ metrics, setMetrics ] = useState( [] );
    const [ cohortLabel, setCohortLabel ] = useState( 'Leads' );

    const cohortRegistry = {
        rate: {
            valueGetter: ({value}) => {
                if (!value) {
                    return value;
                }
                return value * 100;
            },
            valueFormatter: ({value}) => {
                if (value == null || value === '-') return '—';
                return `${value.toLocaleString(undefined, {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1
                })}%`;
            }
        }
    };

    const getConversionSummaryData = useCallback( () => {
        const [startDate, endDate] = getDateRange(filters);
        const cohortField = filters?.cohortStart || 'opp';
        const cohortLabel = reportConfig.milestones?.find( f => f.value === cohortField )?.label || 'Unknown';
        const funnelSteps = Array.isArray( filters?.milestones ) && filters.milestones.length
            ? filters.milestones
            : [ cohortField, 'funded' ];

        const finalStep = funnelSteps[funnelSteps.length - 1];

        const summary = {
            cohortCount: 0,
            convertedCount: 0,
            conversionRate: 0,
            stepCounts: {},
            stepRates: {},
            stepBreakdown: {},
            inferredStepCounts: {},
            inferredStepRates: {},
            totalInferredSteps: 0,
            recordsWithInferredSteps: 0,
        };

        funnelSteps.forEach((step) => {
            summary.stepCounts[step] = 0;
            summary.inferredStepCounts[step] = 0;
            summary.stepRates[step] = 0;
        });

        setFunnelOrder( funnelSteps );
        setCohortLabel( cohortLabel );

        const cohortRecords = [];

        for (const record of data) {
            const milestones = record?.milestones || {};
            const cohortDate = toDate(milestones[cohortField] || record?.[cohortField]);
        
            if (!isWithinRange(cohortDate, startDate, endDate)) continue;
        
            summary.cohortCount += 1;
            cohortRecords.push(record);
        
            // 1️⃣ Find the furthest step reached
            let furthestIndex = -1;
            const actualStepMap = {};
        
            funnelSteps.forEach((step, idx) => {
                const stepDate = toDate(milestones[step] || record?.[step]);
                const isActual = !!stepDate;
                actualStepMap[step] = isActual;

                if (isActual) {
                    furthestIndex = idx;
                }
            });
        
            // 2️⃣ If no steps reached, skip
            if (furthestIndex === -1) continue;
        
            // 3️⃣ Backfill all prior steps
            let inferredCountForRecord = 0;
            const inferredStepMap = {};
            for (let i = 0; i <= furthestIndex; i++) {
                const step = funnelSteps[i];
                const isActual = actualStepMap[step];
                const isInferred = !isActual;
                
                summary.stepCounts[step] += 1;

                inferredStepMap[step] = isInferred;

                if (isInferred) {
                    summary.inferredStepCounts[step] += 1;
                    summary.totalInferredSteps += 1;
                    inferredCountForRecord += 1;
                }
            }
        
            if (inferredCountForRecord > 0) {
                summary.recordsWithInferredSteps += 1;
            }

            record._conversion = {
                furthestStep: funnelSteps[furthestIndex],
                furthestIndex,
                actualStepMap,
                inferredStepMap,
                inferredCount: inferredCountForRecord,
            };

            // 4️⃣ Converted = reached final step
            if (furthestIndex === funnelSteps.length - 1) {
                summary.convertedCount += 1;
            }
        }

        for (const step of funnelSteps) {
            summary.stepRates[step] = summary.cohortCount
                ? (summary.stepCounts[step] / summary.cohortCount)
                : 0;

            summary.inferredStepRates[step] = summary.stepCounts[step]
                ? summary.inferredStepCounts[step] / summary.stepCounts[step]
                : 0;
        }

        summary.conversionRate = summary.cohortCount
            ? (summary.convertedCount / summary.cohortCount)
            : 0;

        
        summary.stepBreakdown = funnelSteps.reduce((acc, step) => {
            acc[step] = {
                label: reportConfig.milestones?.find( f => f.value === step )?.label || 'Unknown',
                value: summary.stepCounts[step]
            };
            return acc;
        }, {});

        let tally = summary?.convertedCount || 0;
        summary.stepDropoffs = {};
        summary.stepConversions = {};
        for ( const step of funnelSteps ) {
            summary.stepDropoffs[step] = tally - summary.stepCounts[step];
            tally = summary.stepCounts[step];
        }

        summary.funnel = [ ...funnelSteps ];

        return {
            summaryData: summary,
            funnelSteps,
            finalStep,
        };
    }, [ data, filters, reportConfig.milestones ] );

    const summaryData = useMemo(() => {
        const EMPTY_SUMMARY_DATA = {
            cohortCount: 0,
            convertedCount: 0,
            conversionRate: 0,
            stepCounts: {},
            stepRates: {},
            stepBreakdown: {},
            trendData: [],
            funnelSteps: [],
            finalStep: null,
        };
        const [startDate, endDate] = getDateRange(filters);
    
        if (!data?.length || !startDate || !endDate) {
            return EMPTY_SUMMARY_DATA;
        }
    
        const summary = getConversionSummaryData();
    
        return {
            ...summary.summaryData,
            trendData: summary.trendData,
            funnelSteps: summary.funnelSteps,
            finalStep: summary.finalStep,
        };
    }, [data, filters, getConversionSummaryData]);

    const breakdownSchema = useMemo(() => {
        const label = cohortLabel.replace(/pre\-?approvals/i, 'PAs');
    
        return [
            { field: 'label', headerName: 'Milestone', flex: 1 },
            { field: 'value', headerName: 'Count' },
            {
                field: 'cohortConversion',
                headerName: `% of ${label}`,
                width: 125,
                type: 'rate',
            },
            {
                field: 'stepConversion',
                headerName: '% of Prior Step',
                width: 150,
                type: 'rate',
            },
        ];
    }, [cohortLabel]);

    // Metrics Bar
    useEffect(() => {
        const { funnel, cohortCount, convertedCount, stepRates, stepDropoffs } = summaryData;
        console.log( 'Metrics summaryData:', summaryData );
        
        if ( !Object.keys( stepRates || {} ).length && !Object.keys( stepDropoffs || {} ).length ) {
            setMetrics( [] );
            return;
        }

        const meta = reportConfig.milestones.reduce( ( acc, item ) => {
            acc[item.value] = { abbr: item.abbr };
            return acc;
        }, {} );
        console.log( 'Metrics meta:', meta );
        
        const transitionDrops = ( funnel || [] ).slice(1).map((step, idx) => {
            const prevStep = funnel[idx];
            return {
                step,
                prevStep,
                value: stepDropoffs[step],
                stepLabel: meta[step]?.abbr || step,
                prevStepLabel: meta[prevStep]?.abbr || prevStep,
                // shortStepLabel: meta[step]?.shortLabel || step,
                // shortPrevStepLabel: meta[prevStep]?.shortLabel || prevStep,
            };
        });
        const biggestDrop = transitionDrops?.reduce((max, curr) =>
            curr.value > max.value ? curr : max
        );
        
        const transitionRates = ( funnel || [] ).slice(1).map((step, idx) => {
            const prevStep = funnel[idx];
            return {
                step,
                prevStep,
                value: stepRates[step],
                stepLabel: meta[step]?.abbr || step,
                prevStepLabel: meta[prevStep]?.abbr || prevStep,
                // shortStepLabel: meta[step]?.shortLabel || step,
                // shortPrevStepLabel: meta[prevStep]?.shortLabel || prevStep,
            };
        });
        const bestRate = transitionRates?.reduce((max, curr) =>
            curr.value > max.value ? curr : max
        );

        setMetrics( [
            {
                label: 'Starting Count',
                value: cohortCount || 0,
                tooltip: '# of records in the selected cohort',
                icon: <FaUsers style={{ color: '#ff7a59', marginRight: 8 }} />,
            },
            {
                label: 'Converted',
                value: convertedCount || 0,
                tooltip: '# of records reaching the final selected milestone',
                icon: <FaFlagCheckered style={{ color: '#ff7a59', marginRight: 8 }} />,
            },
            {
                label: 'Overall Conversion',
                value: formatPercent( convertedCount, cohortCount ),
                tooltip: 'Conversion rate from cohort to final selected milestone',
                icon: <FaPercentage style={{ color: '#ff7a59', marginRight: 8 }} />,
            },
            {
                label: 'Largest Drop-off',
                value: `${biggestDrop?.prevStepLabel} → ${biggestDrop?.stepLabel}`,
                subvalue: `-${biggestDrop?.value} (${formatPercent( stepRates[biggestDrop?.step], 1 )})`,
                tooltip: 'Milestone with largest dropoff from prior step',
                icon: <FaExclamationTriangle style={{ color: '#ff7a59', marginRight: 8 }} />,
            },
            {
                label: 'Best Step',
                value: `${bestRate?.prevStepLabel} → ${bestRate?.stepLabel}`,
                subvalue: `${formatPercent( bestRate?.value || 0, 1 )}`,
                tooltip: '# of milestones in the selected funnel',
                icon: <FaArrowUp style={{ color: '#ff7a59', marginRight: 8 }} />,
            },
        ] );
    }, [ summaryData, reportConfig.milestones ] );

    // Chart/Grid Data
    useEffect( () => {
        const colorMap = [
            '#90caf9',
            '#42a5f5',
            '#1e88e5',
            '#81c784',
            '#66bb6a'
        ];
        const chart = [];
        let cohortTotal = 0;
        funnelOrder.forEach( ( f, idx ) => {
            const id = f || `unknown${idx}`;
            const label = summaryData?.stepBreakdown?.[id]?.label || 'Unknown';
            const color = colorMap[idx];
            const value = summaryData?.stepBreakdown?.[id]?.value || 0;

            if ( idx === 0 ) {
                cohortTotal = value;
            }
            chart.push( {
                id,
                label,
                color,
                value,
                stepConversion: ( idx > 0 ) ? value / chart[idx - 1].value : '-',
                cohortConversion: ( idx > 0 ) ? value / cohortTotal : 1,
            } );
        } );
        setChartData( chart );
    }, [ summaryData, funnelOrder ] );

    return (
        <Container fluid className='w-100'>
            <Row className='pb-4'>
                <Col className='px-0 w-100' sm={12}>
                    <Box className='h-100' display="flex" gap={2} justifyContent="space-evenly" sx={ { flexDirection: { xs: 'column', md: 'row' } } }>
                        <Card variant="outlined" className='py-4 px-0 w-100' style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Stack 
                                direction="row" 
                                justifyContent="space-evenly" 
                                spacing={0} 
                                sx={{ width: 1, flex: 1, minHeight: 0 }} 
                                divider={ <Divider orientation="vertical" flexItem style={ { backgroundColor: 'grey' } } /> }
                            >
                            {
                                metrics.map( ( metric, idx ) => (
                                    <TableMetric key={`metric-${idx}`} metric={metric} variant='stacked' />
                                ) )
                            }
                            </Stack>
                        </Card>
                    </Box>
                </Col>
            </Row>
            <Row className='pb-4 text-center'>
                <Col className='px-0'>
                    <Card className="h-100">
                        <Card.Body>
                            <Row className="d-flex justify-content-between align-items-center mb-2">
                                <Col>
                                    <Card.Title as="h6" className="mb-0">
                                        Conversion Funnel
                                    </Card.Title>
                                </Col>
                            </Row>
                            <Row>
                                <Col style={{ height: '400px' }}>
                                    <ConversionFunnel
                                        data={chartData || []}
                                        height={400}
                                    />
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>

                <Col className='px-0 ps-4'>
                    <Card className="h-100">
                        <Card.Body>
                            <Row className="d-flex justify-content-between align-items-center mb-2">
                                <Col>
                                    <Card.Title as="h6" className="mb-0">
                                        Conversion Breakdown
                                    </Card.Title>
                                </Col>
                            </Row>
                            <Row>
                                <Col style={{ height: '400px' }}>
                                    <ThirdFloorDataGrid 
                                        gridKey={reportConfig.id} 
                                        schema={ { cols: breakdownSchema } } 
                                        pageRegistry={ cohortRegistry } 
                                        rows={chartData} 
                                        overrides={ {
                                            hideFooter: true,
                                            density: 'standard'
                                        } }
                                    />
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className='pb-4'>
                
            </Row>
        </Container>
    );
}